import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'

type AlignType = 'middle' | 'bottom'

type SizeType = 'unknown' | 'small' | 'medium' | 'large' | 'fill'

const getSize = (width?: number) => {
  if (!width) {
    return 'unknown'
  }
  if (width <= 368) {
    return 'small'
  }
  if (width <= 500) {
    return 'medium'
  }
  return 'large'
}

const useSizeClass = (size?: SizeType) => {
  const containerElementRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>()
  const hasCustomSize = !!size
  const sizeClass = hasCustomSize ? size : getSize(width)

  useEffect(() => {
    if (!hasCustomSize) {
      setWidth(containerElementRef.current?.clientWidth ?? 0)
    }
  }, [hasCustomSize, containerElementRef.current?.clientWidth])

  return { sizeClass, containerElementRef }
}

export const NameplateImage = (props: {
  alt?: string
  src?: string
  name?: string
}) => <img className='mc-nameplate__image' {...props} />

interface NameplateProps extends React.HTMLAttributes<HTMLImageElement> {
  image?: React.ReactElement
  src?: string
  alt?: string
  size?: SizeType
  separator?: boolean
  subText?: string
  align?: AlignType
  testId?: string
  name?: string
  logo?: string
  logoAlt?: string
}

const Nameplate = ({
  image,
  src,
  alt = '',
  className = '',
  size,
  separator = false,
  subText = '',
  align = 'middle',
  testId = 'nameplate',
  logo,
  logoAlt,
  ...props
}: NameplateProps) => {
  const { sizeClass, containerElementRef } = useSizeClass(size)

  return (
    <div
      ref={containerElementRef}
      className={cn('mc-nameplate', `mc-nameplate--${sizeClass}`, className)}
      data-testid={testId}
    >
      <div className='mc-nameplate__inner'>
        {logo && (
          <img
            src={logo}
            className='mc-nameplate__logo mc-mb-2'
            alt={logoAlt}
          />
        )}

        {image || <NameplateImage alt={alt} src={src} {...props} />}

        {separator && <div className='mc-nameplate__separator' />}

        {subText && (
          <p
            className={cn(
              'mc-nameplate__sub-text',
              align === 'middle' ? 'mc-text--2-lines' : 'mc-text--2-lines-max',
              {
                'mc-text-h6': sizeClass === 'small',
                'mc-text-h4': sizeClass === 'medium',
                'mc-text-h2': sizeClass === 'large',
              },
            )}
            data-testid={`${testId}__sub-text`}
          >
            {subText}
          </p>
        )}
      </div>
    </div>
  )
}

export default Nameplate
